import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { useAuth } from '../auth/AuthContext.js';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BugReportIcon from '@mui/icons-material/BugReport';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SensorsIcon from '@mui/icons-material/Sensors';
import InsightsIcon from '@mui/icons-material/Insights';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { LANDING_COPY, type Lang, type LandingCopy } from './landingCopy.js';
import { DemoRequestForm } from './DemoRequestForm.js';
import { setLang } from '../i18n/lang.js';

const EMERALD = '#2ec2b3';
const INK = '#0a0f16';

/** Icons pair positionally with copy.features.items — same order, both languages. */
const FEATURE_ICONS = [
  <PlayCircleIcon key="replay" />,
  <ConfirmationNumberIcon key="tickets" />,
  <BugReportIcon key="errors" />,
  <NotificationsActiveIcon key="alerts" />,
  <SensorsIcon key="live" />,
  <PrivacyTipIcon key="privacy" />,
  <InsightsIcon key="analytics" />,
];

const INTEGRATIONS = ['Slack', 'Microsoft Teams', 'Jira', 'Freshdesk', 'ServiceNow', 'GitHub', 'GitLab', 'Datadog', 'New Relic', 'Google Analytics', 'FullStory', 'LogRocket', 'Sentry', 'Segment'];

/** A public storefront running the SDK — the fastest way to see a real replay. */
const LIVE_DEMO_URL = 'https://shop.coverops.ai/';

interface PriceCard {
  name: string;
  price: string;
  unit: string;
  tagline: string;
  features: string[];
  cta: string;
  /** Paid packages are quoted, so their button asks for a conversation. */
  talk: boolean;
  highlight: boolean;
}

/** Shape of a plan as the public catalogue endpoint returns it: no price. */
interface PublicPlan {
  code: string;
  name: string;
  free: boolean;
  monthlySessions: number | null;
  dailySessions: number | null;
  maxProjects: number | null;
  maxIntegrations: number | null;
  integrationsEnabled: boolean;
  coverOps: string;
  retentionDays: number;
  testerEnabled: boolean;
  testerSeats: number;
  piiMasking: boolean;
  ingestPiiMasking: boolean;
  ssoEnabled: boolean;
}

/**
 * Last-resort cards for when the catalogue can't be reached (API down, or the
 * marketing page served without a backend). Matches the seeded catalogue; the
 * live one always wins when it answers.
 */
const plan = (p: Partial<PublicPlan> & Pick<PublicPlan, 'code' | 'name'>): PublicPlan => ({
  free: false, monthlySessions: null, dailySessions: null, maxProjects: null, maxIntegrations: null,
  integrationsEnabled: true, coverOps: 'none', retentionDays: 30, testerEnabled: true, testerSeats: 0,
  piiMasking: false, ingestPiiMasking: false, ssoEnabled: false, ...p,
});
const FALLBACK_PLANS: PublicPlan[] = [
  plan({ code: 'free', name: 'Free', free: true, monthlySessions: 1000, maxProjects: 1, maxIntegrations: 0, integrationsEnabled: false, retentionDays: 7, testerEnabled: false }),
  plan({ code: 'startup', name: 'Startup', monthlySessions: 25000, maxProjects: 1, maxIntegrations: 3, retentionDays: 30 }),
  plan({ code: 'pro', name: 'Pro', monthlySessions: 100000, maxProjects: 3, maxIntegrations: 10, coverOps: 'addon', retentionDays: 60 }),
  plan({ code: 'business', name: 'Business', monthlySessions: 500000, maxProjects: 10, maxIntegrations: 25, coverOps: 'included', retentionDays: 90, piiMasking: true, ingestPiiMasking: true }),
  plan({ code: 'enterprise', name: 'Enterprise', monthlySessions: 2000000, maxProjects: 25, maxIntegrations: 30, coverOps: 'included', retentionDays: 365, piiMasking: true, ingestPiiMasking: true, ssoEnabled: true }),
];

/** Turns a catalogue row into the card the pricing section renders. */
function toPriceCard(p: PublicPlan, c: LandingCopy): PriceCard {
  const t = c.pricing;
  const L = t.limits;
  const n = (v: number) => v.toLocaleString(c.htmlLang);
  const sessions =
    p.monthlySessions !== null
      ? L.sessionsPerMonth(n(p.monthlySessions))
      : p.dailySessions !== null
        ? L.sessionsPerDay(n(p.dailySessions))
        : L.unlimitedSessions;
  const tools = p.integrationsEnabled && p.maxIntegrations !== 0;
  const features = [
    sessions,
    p.maxProjects === null ? L.unlimitedProjects : L.projects(p.maxProjects),
    L.retention(p.retentionDays),
    L.core,
    ...(tools ? [p.maxIntegrations === null ? L.integrations : L.integrationTools(p.maxIntegrations)] : []),
    ...(p.testerEnabled ? [p.testerSeats > 0 ? L.testerSeats(p.testerSeats) : L.testerUnlimited] : []),
    ...(p.coverOps === 'included' ? [L.coverOps] : p.coverOps === 'addon' ? [L.coverOpsAddon] : []),
    ...(p.piiMasking || p.ingestPiiMasking ? [L.piiMasking] : []),
    ...(p.ssoEnabled ? [L.sso] : []),
    ...(t.extras[p.code] ?? []),
  ];
  return {
    name: p.name,
    // Prices are quoted by the team (owner's call, 2026-10-22); only Free,
    // which costs nothing, says what it costs.
    price: p.free ? '$0' : t.priceOnRequest,
    unit: p.free ? t.perMonth : '',
    tagline: t.taglines[p.code] ?? '',
    features,
    cta: p.free ? t.ctaFree : t.ctaCustom,
    talk: !p.free,
    // Pro is the featured tier; the catalogue has no such flag.
    highlight: p.code === 'pro',
  };
}

export function LandingPage({ lang = 'en' }: { lang?: Lang }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const go = () => navigate(isAuthenticated ? '/dashboard' : '/register');
  const signIn = () => navigate('/login');
  const c = LANDING_COPY[lang];

  // Each language is served from its own static shell (see
  // scripts/build-lang-shells.mjs), which carries the hreflang alternates —
  // those are identical on both pages and never change here. What DOES change
  // is everything a client-side language switch affects, since no document is
  // reloaded: <html lang>, the title, the description and the canonical URL.
  // Remember the choice for the pages that carry no language in their URL.
  // Someone who read the Turkish landing page and then clicked "Sign in"
  // should not land on an English form.
  useEffect(() => {
    setLang(lang);
  }, [lang]);

  useEffect(() => {
    const previousLang = document.documentElement.lang;
    document.documentElement.lang = c.htmlLang;

    // The shell ships a canonical tag and an English title; update them in
    // place rather than appending duplicates a crawler would have to resolve.
    const previousTitle = document.title;
    document.title = c.seo.title;
    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDesc = metaDesc?.content;
    if (metaDesc) metaDesc.content = c.seo.description;

    const origin = window.location.origin;
    const canonicalHref = `${origin}${lang === 'tr' ? '/tr' : '/'}`;
    const canonical =
      document.querySelector<HTMLLinkElement>('link[rel="canonical"]') ?? document.createElement('link');
    const canonicalExisted = canonical.isConnected;
    const previousCanonical = canonical.href;
    canonical.rel = 'canonical';
    canonical.href = canonicalHref;
    if (!canonicalExisted) document.head.appendChild(canonical);

    return () => {
      document.documentElement.lang = previousLang;
      document.title = previousTitle;
      if (metaDesc && previousDesc !== undefined) metaDesc.content = previousDesc;
      if (canonicalExisted) canonical.href = previousCanonical;
      else canonical.remove();
    };
  }, [c.htmlLang, lang]);

  // Pricing is the live catalogue (Platform Admin → Plans), so an operator's
  // edit is what visitors see. Cheap, cacheable, and unauthenticated.
  const plans = useQuery({
    queryKey: ['public-plans'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/v1/public/plans')).data.data as PublicPlan[],
  });
  // Only fall back when the catalogue can't be read — an operator who disabled
  // every plan should see an empty section, not resurrected defaults.
  const pricing = (plans.isError ? FALLBACK_PLANS : (plans.data ?? [])).map((p) => toPriceCard(p, c));

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* ── Nav ─────────────────────────────────────────── */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'rgba(246,247,249,0.8)', backdropFilter: 'blur(10px)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 64 }}>
            <PlayCircleIcon sx={{ color: EMERALD }} />
            <Typography variant="h6" sx={{ fontWeight: 800, flexGrow: 1, color: INK }}>
              Replay<Box component="span" sx={{ color: EMERALD }}>Ops</Box>
            </Typography>
            {c.nav.links.map((l) => (
              <Button key={l.id} href={`#${l.id}`} sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' }, '&:hover': { color: INK } }}>{l.label}</Button>
            ))}
            {/* A route, not an in-page anchor. The docs are their own pages. */}
            <Button
              component={RouterLink}
              to={lang === 'tr' ? '/tr/docs' : '/docs'}
              sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' }, '&:hover': { color: INK } }}
            >
              {c.nav.docs}
            </Button>
            <LangSwitch lang={lang} copy={c.langSwitch} />
            {isAuthenticated ? (
              <Button variant="contained" onClick={() => navigate('/dashboard')} endIcon={<ArrowForwardIcon />} sx={{ bgcolor: EMERALD, color: INK, '&:hover': { bgcolor: '#22988c' } }}>{c.nav.dashboard}</Button>
            ) : (
              <>
                <Button onClick={signIn} sx={{ color: INK }}>{c.nav.signIn}</Button>
                <Button variant="contained" onClick={go} sx={{ bgcolor: EMERALD, color: INK, '&:hover': { bgcolor: '#22988c' } }}>{c.nav.getStarted}</Button>
              </>
            )}
          </Stack>
        </Container>
      </Box>

      {/* ── Hero (light) ────────────────────────────────── */}
      <Box sx={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #eef5f4 0%, #f6f7f9 60%)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 1000, height: 520, background: `radial-gradient(closest-side, ${EMERALD}26, transparent)`, pointerEvents: 'none' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 8, md: 13 }, textAlign: 'center' }}>
          <Chip label={c.hero.badge} sx={{ mb: 3, bgcolor: 'rgba(46,194,179,0.12)', color: '#1a8f83', fontWeight: 600, border: `1px solid ${EMERALD}55` }} />
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 38, md: 62 }, lineHeight: 1.05, letterSpacing: -1.5, mb: 3, color: INK }}>
            {c.hero.titleTop}
            <Box component="span" sx={{ display: 'block', color: EMERALD }}>{c.hero.titleAccent}</Box>
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: { xs: 16, md: 20 }, maxWidth: 700, mx: 'auto', mb: 4 }}>
            {c.hero.subtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 2 }}>
            <Button size="large" variant="contained" onClick={go} endIcon={<ArrowForwardIcon />} sx={{ px: 4, py: 1.25, bgcolor: EMERALD, color: INK, fontSize: 16, '&:hover': { bgcolor: '#22988c' } }}>{c.hero.ctaPrimary}</Button>
            {/* A real storefront running the SDK. Seeing your own session
                replayed is more convincing than any screenshot. */}
            <Button
              size="large"
              href={LIVE_DEMO_URL}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon />}
              sx={{ px: 4, py: 1.25, color: INK, border: '1px solid', borderColor: 'divider', bgcolor: '#fff', fontSize: 16, '&:hover': { borderColor: INK } }}
            >
              {c.hero.ctaDemo}
            </Button>
            <Button size="large" href="#howitworks" sx={{ px: 4, py: 1.25, color: INK, fontSize: 16 }}>{c.hero.ctaHow}</Button>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.hero.caption}</Typography>

          {/* Product mockup */}
          <Box sx={{ mt: 7, mx: 'auto', maxWidth: 940 }}>
            <HeroMock />
          </Box>
        </Container>
      </Box>

      {/* ── Outcomes ────────────────────────────────────── */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 3, md: 2 } }}>
            {c.outcomes.items.map((o) => (
              <Box key={o.label} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 36 }, color: EMERALD, lineHeight: 1.1 }}>
                  {o.figure}
                  {o.note && (
                    <Box component="span" sx={{ fontSize: 14, verticalAlign: 'super', color: 'text.disabled', ml: 0.3 }}>
                      *
                    </Box>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{o.label}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'text.disabled', textAlign: { xs: 'left', md: 'center' } }}>
            {c.outcomes.footnote}
          </Typography>
        </Container>
      </Box>

      {/* ── Logos strip ─────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography align="center" variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase' }}>
          {c.logosTitle}
        </Typography>
        <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.5} sx={{ mt: 2 }}>
          {INTEGRATIONS.map((n) => (
            <Chip key={n} label={n} variant="outlined" sx={{ fontWeight: 600, color: 'text.secondary' }} />
          ))}
        </Stack>
      </Container>

      {/* ── Features ────────────────────────────────────── */}
      <Container id="features" maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <SectionHead eyebrow={c.features.eyebrow} title={c.features.title} subtitle={c.features.subtitle} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5, mt: 5 }}>
          {c.features.items.map((f, i) => (
            <Box key={f.title} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', transition: 'border-color .15s, transform .15s', '&:hover': { borderColor: EMERALD, transform: 'translateY(-3px)' } }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'grid', placeItems: 'center', color: EMERALD, bgcolor: `${EMERALD}1f`, mb: 2 }}>{FEATURE_ICONS[i]}</Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{f.title}</Typography>
              <Typography variant="body2" color="text.secondary">{f.body}</Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── How it works ────────────────────────────────── */}
      <Box id="howitworks" sx={{ bgcolor: 'action.hover', py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="lg">
          <SectionHead eyebrow={c.steps.eyebrow} title={c.steps.title} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3, mt: 5 }}>
            {c.steps.items.map((s) => (
              <Box key={s.n}>
                <Typography sx={{ fontWeight: 800, fontSize: 34, color: EMERALD, opacity: 0.5, mb: 1 }}>{s.n}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{s.title}</Typography>
                <Typography variant="body2" color="text.secondary">{s.body}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Dynamic masking (GDPR / KVKK) ───────────────── */}
      <Box id="privacy" sx={{ bgcolor: 'action.hover', py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="lg">
          <SectionHead eyebrow={c.privacy.eyebrow} title={c.privacy.title} subtitle={c.privacy.subtitle} />
          <Box sx={{ maxWidth: 980, mx: 'auto', mt: 5 }}>
            <MaskingDemo copy={c.privacy} />
          </Box>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 4, maxWidth: 980, mx: 'auto' }}
          >
            {c.privacy.cards.map((card) => (
              <Box key={card.t} sx={{ flex: 1, p: 3, borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>{card.t}</Typography>
                <Typography variant="body2" color="text.secondary">{card.b}</Typography>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
            {c.privacy.chips.map((label) => (
              <Chip key={label} size="small" label={label} sx={{ bgcolor: `${EMERALD}1f`, color: '#1a8f83', fontWeight: 600 }} />
            ))}
          </Stack>

          {/* Compliance posture. The questions an enterprise buyer asks first. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 5, maxWidth: 980, mx: 'auto' }}>
            {c.privacy.compliance.map((item) => (
              <Box key={item.t} sx={{ p: 2.5, borderRadius: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <VerifiedUserIcon sx={{ fontSize: 18, color: EMERALD }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{item.t}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{item.b}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Self-hosted ─────────────────────────────────── */}
      <Container id="selfhosted" maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Box sx={{ borderRadius: 4, overflow: 'hidden', bgcolor: INK, color: '#fff', position: 'relative' }}>
          <Box sx={{ position: 'absolute', bottom: -140, right: -80, width: 500, height: 400, background: `radial-gradient(closest-side, ${EMERALD}2e, transparent)` }} />
          <Box sx={{ position: 'relative', p: { xs: 4, md: 7 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 5, alignItems: 'center' }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: EMERALD }}>
                <ShieldIcon />
                <Typography variant="overline" sx={{ letterSpacing: 1.5 }}>{c.selfHosted.overline}</Typography>
              </Stack>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 }, lineHeight: 1.15, mb: 2 }}>
                {c.selfHosted.title}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
                {c.selfHosted.body}
              </Typography>
              <Button variant="contained" onClick={go} sx={{ bgcolor: EMERALD, '&:hover': { bgcolor: '#22988c' } }}>{c.selfHosted.cta}</Button>
            </Box>
            <Stack spacing={1.5}>
              {c.selfHosted.bullets.map((t) => (
                <Stack key={t} direction="row" spacing={1.5} alignItems="center">
                  <CheckIcon sx={{ color: EMERALD, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* ── Why ReplayOps ───────────────────────────────── */}
      <Container id="why" maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <SectionHead eyebrow={c.why.eyebrow} title={c.why.title} subtitle={c.why.subtitle} />
        <Box sx={{ maxWidth: 900, mx: 'auto', mt: 5 }}>
          {c.why.items.map((item, i) => (
            <Stack
              key={item.claim}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 3 }}
              sx={{ py: 3, borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider' }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: { sm: 300 }, flexShrink: 0 }}>
                <CheckIcon sx={{ color: EMERALD, fontSize: 20, mt: '2px' }} />
                <Typography sx={{ fontWeight: 700 }}>{item.claim}</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">{item.body}</Typography>
            </Stack>
          ))}
        </Box>
      </Container>

      {/* ── Pricing ─────────────────────────────────────── */}
      <Container id="pricing" maxWidth="xl" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <SectionHead eyebrow={c.pricing.eyebrow} title={c.pricing.title} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: `repeat(${Math.min(Math.max(pricing.length, 1), 5)}, 1fr)` }, gap: 2.5, mt: 5, alignItems: 'start' }}>
          {pricing.map((p) => (
            <Box key={p.name} sx={{ p: 3, borderRadius: 3, border: '2px solid', borderColor: p.highlight ? EMERALD : 'divider', bgcolor: 'background.paper', position: 'relative', boxShadow: p.highlight ? '0 12px 40px rgba(46,194,179,0.15)' : 'none' }}>
              {p.highlight && <Chip label={c.pricing.popular} size="small" sx={{ position: 'absolute', top: -12, left: 24, bgcolor: EMERALD, color: '#fff', fontWeight: 700 }} />}
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{p.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>{p.tagline}</Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 36 }}>{p.price}</Typography>
                <Typography color="text.secondary">{p.unit}</Typography>
              </Stack>
              <Button fullWidth variant={p.highlight ? 'contained' : 'outlined'} onClick={p.talk ? () => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' }) : go} sx={p.highlight ? { mb: 2.5, bgcolor: EMERALD, '&:hover': { bgcolor: '#22988c' } } : { mb: 2.5, borderColor: EMERALD, color: EMERALD }}>{p.cta}</Button>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.25}>
                {p.features.map((f) => (
                  <Stack key={f} direction="row" spacing={1} alignItems="center">
                    <CheckIcon sx={{ color: EMERALD, fontSize: 18 }} />
                    <Typography variant="body2">{f}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── FAQ ─────────────────────────────────────────── */}
      <Box id="faq" sx={{ bgcolor: 'action.hover', py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
        <Container maxWidth="md">
          <SectionHead eyebrow={c.faq.eyebrow} title={c.faq.title} />
          <Box sx={{ mt: 5 }}>
            {c.faq.items.map((f) => (
              <Accordion
                key={f.q}
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { borderColor: EMERALD },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Typography variant="body2" color="text.secondary">{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Demo request ────────────────────────────────── */}
      <DemoRequestForm copy={c.demo} lang={lang} />

      {/* ── Final CTA ───────────────────────────────────── */}
      <Box sx={{ bgcolor: INK, color: '#fff' }}>
        <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 }, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 40 }, mb: 2 }}>
            {c.finalCta.title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 4 }}>
            {c.finalCta.body}
          </Typography>
          <Button size="large" variant="contained" onClick={go} endIcon={<ArrowForwardIcon />} sx={{ px: 5, py: 1.4, bgcolor: EMERALD, fontSize: 16, '&:hover': { bgcolor: '#22988c' } }}>{c.finalCta.cta}</Button>
        </Container>
      </Box>

      {/* ── Footer ──────────────────────────────────────── */}
      <Box sx={{ bgcolor: INK, color: 'rgba(255,255,255,0.6)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3, pb: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.45)', letterSpacing: 1.5 }}>
              {c.footer.deckTitle}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <DeckLink href={lang === 'tr' ? '/tr/docs' : '/docs'}>{c.nav.docs}</DeckLink>
              <DeckLink href="/decks/replayops-tr.html">Deck · TR</DeckLink>
              <DeckLink href="/decks/ReplayOps-Sunum-TR.pdf">PDF · TR</DeckLink>
              <DeckLink href="/decks/replayops-en.html">Deck · EN</DeckLink>
              <DeckLink href="/decks/ReplayOps-Sales-Deck-EN.pdf">PDF · EN</DeckLink>
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PlayCircleIcon sx={{ color: EMERALD, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700 }}>ReplayOps</Typography>
            </Stack>
            <Typography variant="caption">© {new Date().getFullYear()} ReplayOps · {c.footer.tagline}</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

/**
 * EN/TR switch. Real links rather than a state toggle, so each language has
 * its own URL that can be shared, bookmarked and indexed.
 */
function LangSwitch({ lang, copy }: { lang: Lang; copy: LandingCopy['langSwitch'] }) {
  const item = (to: string, label: string, active: boolean) => (
    <Button
      component={RouterLink}
      to={to}
      size="small"
      aria-current={active ? 'page' : undefined}
      sx={{
        minWidth: 40,
        px: 1,
        fontWeight: 700,
        color: active ? INK : 'text.disabled',
        '&:hover': { color: INK, bgcolor: 'transparent' },
      }}
    >
      {label}
    </Button>
  );
  return (
    <Stack direction="row" alignItems="center" aria-label={copy.label} sx={{ mr: { xs: 0, md: 1 } }}>
      {item('/', copy.toEn, lang === 'en')}
      <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
      {item('/tr', copy.toTr, lang === 'tr')}
    </Stack>
  );
}

/** Footer link to a hosted sales deck (HTML or PDF), served from /public/decks. */
function DeckLink({ href, children }: { href: string; children: string }) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noopener"
      size="small"
      variant="outlined"
      sx={{
        color: 'rgba(255,255,255,0.85)',
        borderColor: 'rgba(255,255,255,0.18)',
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': { borderColor: EMERALD, color: EMERALD, bgcolor: 'transparent' },
      }}
    >
      {children}
    </Button>
  );
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

function MaskingDemo({ copy }: { copy: LandingCopy['privacy'] }) {
  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      }}
    >
      {/* Before. What the visitor actually saw */}
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderRight: { md: '1px solid' }, borderBottom: { xs: '1px solid', md: 'none' }, borderColor: { xs: 'divider', md: 'divider' } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <VisibilityIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'text.secondary' }}>
            {copy.beforeLabel}
          </Typography>
        </Stack>
        <Stack spacing={2.5}>
          {copy.rows.map((r) => (
            <Box key={r.label}>
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>{r.label}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 13.5, color: 'text.primary', wordBreak: 'break-word' }}>
                {r.before}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* After, what ReplayOps keeps */}
      <Box sx={{ p: { xs: 2.5, md: 3.5 }, bgcolor: INK, color: '#fff', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: -60, right: -40, width: 280, height: 240, background: `radial-gradient(closest-side, ${EMERALD}33, transparent)`, pointerEvents: 'none' }} />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5, position: 'relative' }}>
          <VisibilityOffIcon sx={{ fontSize: 18, color: EMERALD }} />
          <Typography variant="overline" sx={{ letterSpacing: 1.2, color: EMERALD }}>
            {copy.afterLabel}
          </Typography>
        </Stack>
        <Stack spacing={2.5} sx={{ position: 'relative' }}>
          {copy.rows.map((r) => (
            <Box key={r.label}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 0.5 }}>{r.label}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 13.5, wordBreak: 'break-word' }}>
                {splitMasked(r.after).map((part, i) =>
                  part.masked ? (
                    <Box
                      key={i}
                      component="span"
                      sx={{ bgcolor: `${EMERALD}2e`, color: EMERALD, border: `1px solid ${EMERALD}66`, borderRadius: 1, px: 0.6, py: 0.15, fontWeight: 600 }}
                    >
                      {part.text}
                    </Box>
                  ) : (
                    <Box key={i} component="span" sx={{ color: 'rgba(255,255,255,0.75)' }}>{part.text}</Box>
                  ),
                )}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

/** Splits masked output so the `<LABEL>` placeholders can be highlighted. */
function splitMasked(text: string): { text: string; masked: boolean }[] {
  const out: { text: string; masked: boolean }[] = [];
  const re = /<[A-Z_]+>/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), masked: false });
    out.push({ text: m[0], masked: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), masked: false });
  return out;
}

function SectionHead({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) {
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
      <Typography variant="overline" sx={{ color: EMERALD, fontWeight: 700, letterSpacing: 1.5 }}>{eyebrow}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 38 }, letterSpacing: -0.5, mt: 0.5 }}>{title}</Typography>
      {subtitle && <Typography color="text.secondary" sx={{ mt: 1.5 }}>{subtitle}</Typography>}
    </Box>
  );
}

/** A stylized dashboard preview for the hero — evokes the product without an image. */
function HeroMock() {
  const kpis = [
    { label: 'Open tickets', value: '128', color: EMERALD },
    { label: 'High-frustration', value: '43', color: '#ef4444' },
    { label: 'Open errors', value: '17', color: '#f59e0b' },
    { label: 'Alerts (24h)', value: '6', color: '#f43f5e' },
  ];
  const bars = [92, 64, 20, 20, 18, 12];
  const track = '#eceff3';
  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e5ea', bgcolor: '#ffffff', boxShadow: '0 24px 60px -12px rgba(19,23,32,0.18)', textAlign: 'left' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eceff3', bgcolor: '#f6f7f9' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: EMERALD }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>ReplayOps · Overview</Typography>
      </Stack>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
          {kpis.map((k) => (
            <Box key={k.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eceff3' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 22, color: k.color }}>{k.value}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{k.label}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 2 }}>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eceff3' }}>
            <Typography variant="caption" sx={{ color: INK, fontWeight: 700 }}>Most frustrated sessions</Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {bars.map((w, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ color: w >= 70 ? '#ef4444' : w >= 40 ? '#f59e0b' : 'text.secondary', width: 30, fontWeight: 700 }}>{w}%</Typography>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: track, overflow: 'hidden' }}>
                    <Box sx={{ width: `${w}%`, height: '100%', bgcolor: w >= 70 ? '#ef4444' : w >= 40 ? '#f59e0b' : EMERALD }} />
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #eceff3' }}>
            <Typography variant="caption" sx={{ color: INK, fontWeight: 700 }}>Recent alerts</Typography>
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              {['Error spike on /checkout', 'Rage-click surge · /payment', 'Frustration up 12%'].map((t) => (
                <Stack key={t} direction="row" spacing={1} alignItems="center">
                  <LocalFireDepartmentIcon sx={{ color: '#ef4444', fontSize: 15 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
